import type { AgentDeal, Region, UserAgreement } from '@repo/database/src/types';
import { Prisma } from '@repo/database/src/types';
import { Transform, Type } from 'class-transformer';
import { IsBoolean, IsEnum, IsObject, IsOptional, IsString, IsUUID, ValidateNested } from 'class-validator';

import { AgentDealStatus } from '../../agent-deal-status';
import { PaginationOptionsDto } from '../../pagination-options-types';

export interface AgentUserAgreementDto {
  id: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  type: UserAgreement['agreement'];
  rakeSharePercentage?: number;
}

export interface AgentDealDto
  extends Omit<AgentDeal, 'createdAt' | 'updatedAt' | 'startDate' | 'endDate' | 'deletedAt' | 'bonus'> {
  startDate: string;
  endDate: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
}

/**
 * Filter DTO for agents in the admin dashboard
 */
export class AdminAgentsFilterDto {
  @IsOptional()
  @IsString()
  search?: string;

  @IsOptional()
  @IsEnum(['Override', 'Flat', 'No Deal'])
  dealType?: 'Override' | 'Flat' | 'No Deal';

  @IsOptional()
  @IsString()
  userAgreementType?: string;

  @IsOptional()
  @IsBoolean()
  @Transform(({ value }) => value === true || value === 'true')
  isRegionalManager?: boolean; // Using string to match the pattern (will be converted to boolean in service)

  @IsOptional()
  @Transform(({ value }) => {
    if (value === undefined || value === null || value === '') return undefined;
    return Array.isArray(value) ? value : [value];
  })
  @IsEnum(AgentDealStatus, { each: true })
  dealStatus?: AgentDealStatus[] | AgentDealStatus;

  @IsOptional()
  @IsString()
  alias?: string;

  @IsOptional()
  @IsString()
  email?: string;

  @IsOptional()
  @IsUUID('4')
  regionId?: string;
}

/**
 * Main DTO for getting agents list with filters, pagination, and sorting
 */
export class GetAdminAgentsDto extends PaginationOptionsDto {
  @IsObject()
  @ValidateNested()
  @IsOptional()
  @Type(() => AdminAgentsFilterDto)
  filter?: AdminAgentsFilterDto;

  @IsOptional()
  sorts?: Array<{ field: string; order: Prisma.SortOrder }>;
}

/**
 * Response DTO for agents list with computed fields
 */
export interface AdminAgentInfoDto {
  // User information
  id: string;
  alias: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isAgent: boolean;
  createdAt: string;
  lastLogin?: string;

  // Current UserAgreement (for context)
  currentUserAgreement?: AgentUserAgreementDto;

  // Agent Deal information (if exists)
  agentDeal?: AgentDealDto;

  // Computed fields
  dealStatus: AgentDealStatus;
  hasAgentDeal: boolean;

  // Regional information (if regional manager)
  region?: Region | null;
}

/**
 * Response DTO for admin agents list
 */
export interface AdminAgentsResponseDto {
  agents: AdminAgentInfoDto[];
  totalCount: number;
  skip: number;
  take: number;
}
